import React, { useEffect, useState } from 'react';
import { DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Input, notification, Row, Space, Spin } from 'antd';
import { useIntl } from 'react-intl';
import { ObtPartnerLegal, ObtUserRestriction } from '../../../__generated__/types';
import {
    useObt_UpdateLegalDetailsMutation,
    useObt_DeletePartnerLegalDetailMutation,
} from '../obtQueries.generated';

// ─── Constants ─────────────────────────────────────────────────────────────────

const LEGAL_TAB_NAME = 'ITALY';

// ─── Types ─────────────────────────────────────────────────────────────────────

type ObtItalyLegalFormProps = {
    partnerLegalData: ObtPartnerLegal;
    partnerno: string;
    companycode: string;
    idpartner: number;
    canUpdate: boolean;
    userRestrictions: ObtUserRestriction[];
};

// ─── Helpers ───────────────────────────────────────────────────────────────────

function hasLegalRestrictionAccess(restrictions: ObtUserRestriction[], tabName: string): boolean {
    if (!restrictions || restrictions.length === 0) return true;
    return restrictions.some((r) => r.legalTabName === tabName);
}

// ─── Component ─────────────────────────────────────────────────────────────────

const ObtItalyLegalForm: React.FC<ObtItalyLegalFormProps> = ({
    partnerLegalData,
    partnerno,
    companycode,
    idpartner,
    canUpdate,
    userRestrictions,
}) => {
    const intl = useIntl();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const [updateLegal] = useObt_UpdateLegalDetailsMutation();
    const [deleteLegal] = useObt_DeletePartnerLegalDetailMutation();

    useEffect(() => {
        if (partnerLegalData) {
            form.setFieldsValue({
                localcode: partnerLegalData.localcode ?? '',
                codicedest: partnerLegalData.codicedest ?? '',
                pecaddress: partnerLegalData.pecaddress ?? '',
            });
        } else {
            form.resetFields();
        }
    }, [partnerLegalData, form]);

    const canAccess = hasLegalRestrictionAccess(userRestrictions, LEGAL_TAB_NAME);

    const handleSave = async () => {
        if (!canAccess) {
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.restriction.error', defaultMessage: 'You do not have permission for this legal tab.' }),
            });
            return;
        }

        const values = form.getFieldsValue();
        setLoading(true);
        try {
            await updateLegal({
                variables: {
                    legalModel: {
                        idpartner,
                        partnerno,
                        companycode,
                        codicedest: values.codicedest || '',
                        localcode: values.localcode || '',
                        pecaddress: values.pecaddress || '',
                    },
                },
            });
            notification.success({
                message: intl.formatMessage({ id: 'obt.legal.italy.save.success', defaultMessage: 'Italy legal details saved successfully.' }),
            });
        } catch (err) {
            console.error('Failed to save Italy legal data:', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.italy.save.error', defaultMessage: 'Failed to save Italy legal details.' }),
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!canAccess) {
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.restriction.error', defaultMessage: 'You do not have permission for this legal tab.' }),
            });
            return;
        }

        setLoading(true);
        try {
            await deleteLegal({
                variables: {
                    legalModel: {
                        idpartner,
                        partnerno,
                        companycode,
                        codicedest: '',
                        localcode: '',
                        pecaddress: '',
                    },
                },
            });
            form.resetFields();
            notification.success({
                message: intl.formatMessage({ id: 'obt.legal.italy.delete.success', defaultMessage: 'Italy legal details deleted successfully.' }),
            });
        } catch (err) {
            console.error('Failed to delete Italy legal data:', err);
            notification.error({
                message: intl.formatMessage({ id: 'obt.legal.italy.delete.error', defaultMessage: 'Failed to delete Italy legal details.' }),
            });
        } finally {
            setLoading(false);
        }
    };

    const isDisabled = !canUpdate || !canAccess;

    return (
        <Spin spinning={loading} size="large">
            <Form layout="vertical" onFinish={handleSave} form={form}>
                <Card
                    title={intl.formatMessage({ id: 'obt.legal.italy.title', defaultMessage: 'Italy Legal Details' })}
                    size="small"
                    
                >
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="localcode"
                                label={intl.formatMessage({ id: 'obt.legal.italy.localcode', defaultMessage: 'Local Code' })}
                            >
                                <Input
                                    placeholder={intl.formatMessage({ id: 'obt.legal.italy.localcode', defaultMessage: 'Local Code' })}
                                    disabled={isDisabled}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="codicedest"
                                label={intl.formatMessage({ id: 'obt.legal.italy.codicedest', defaultMessage: 'Codice Destinatario' })}
                            >
                                <Input
                                    placeholder={intl.formatMessage({ id: 'obt.legal.italy.codicedest', defaultMessage: 'Codice Destinatario' })}
                                    disabled={isDisabled}
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item
                                name="pecaddress"
                                label={intl.formatMessage({ id: 'obt.legal.italy.pecaddress', defaultMessage: 'PEC Address' })}
                            >
                                <Input
                                    placeholder={intl.formatMessage({ id: 'obt.legal.italy.pecaddress', defaultMessage: 'PEC Address' })}
                                    disabled={isDisabled}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col>
                            <Space>
                                <Button
                                    icon={<SaveOutlined />}
                                    type="primary"
                                    htmlType="submit"
                                    disabled={isDisabled}
                                >
                                    {intl.formatMessage({ id: 'obt.legal.btn.save', defaultMessage: 'Save' })}
                                </Button>
                                <Button
                                    icon={<DeleteOutlined />}
                                    onClick={handleDelete}
                                    disabled={isDisabled}
                                >
                                    {intl.formatMessage({ id: 'obt.legal.btn.delete', defaultMessage: 'Delete' })}
                                </Button>
                            </Space>
                        </Col>
                    </Row>
                </Card>
            </Form>
        </Spin>
    );
};

export default ObtItalyLegalForm;

